import { ComponentProps, FC } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { textVariants } from "./RmText/variants";

export const RmTooltipProvider: FC<
  ComponentProps<typeof TooltipPrimitive.Provider>
> = ({ delayDuration = 0, ...props }) => {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
};

export const RmTooltip: FC<
  ComponentProps<typeof TooltipPrimitive.Root>
> = props => {
  return (
    <RmTooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </RmTooltipProvider>
  );
};

export const RmTooltipTrigger: FC<
  ComponentProps<typeof TooltipPrimitive.Trigger>
> = props => {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
};

type RmTooltipContentPropsType = ComponentProps<
  typeof TooltipPrimitive.Content
> &
  ChildrenOrI18nType;

export const RmTooltipContent: FC<RmTooltipContentPropsType> = ({
  className,
  sideOffset = 0,
  i18nKey,
  i18nOptions,
  children,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-fit max-w-80 animate-in rounded-md bg-primary px-3 py-1.5 whitespace-pre-line text-primary-foreground fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          textVariants({ variant: "sm" }),
          className,
        )}
        {...props}>
        {children ?? i18nText}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-primary fill-primary" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};
